/** Labour Management (Demo Mode only) - workforce control workspace for a multi-site
 * contract catering operator (Thomas Franks-style). No backend rota/scheduling/training
 * feed exists yet - all figures are static mock data (`data.ts`), matching the "seeded
 * illustrative numbers" approach used by `src/pages/FinanceDashboard`. Local UI state
 * (resolving exceptions, adding notes, requesting cover) mutates component state only and
 * is not persisted - the goal is to demonstrate the target workflow, not the backend. */

import { useEffect, useMemo, useState } from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import useCurrencyCodeConfiguration from '../../hooks/configurations/useCurrencyCodeConfiguration';
import FiltersBar, { ALL_FILTER_VALUE, LabourFilters } from './components/FiltersBar';
import KpiCard from './components/KpiCard';
import RiskCard from './components/RiskCard';
import CoverageTable from './components/CoverageTable';
import RotaTable from './components/RotaTable';
import WeeklyTrend from './components/WeeklyTrend';
import DemandAlignmentTable from './components/DemandAlignmentTable';
import AbsenceCoverTable from './components/AbsenceCoverTable';
import ReadinessPanel from './components/ReadinessPanel';
import SiteDrilldownDialog from './components/SiteDrilldownDialog';
import {
  ABSENCE_COVER,
  COVERAGE_ROWS,
  DEMAND_ALIGNMENT,
  KPI_CARDS,
  LABOUR_RISKS,
  READINESS_ISSUES,
  READINESS_METRICS,
  ROTA_ROWS,
  SITE_DRILLDOWNS,
  SITE_SECTORS,
  WEEKLY_TREND,
} from './data';
import { AbsenceCoverRow, LabourRisk, LabourTab, RotaRow } from './types';

const TABS: { key: LabourTab; labelKey: string; label: string }[] = [
  { key: 'overview', labelKey: 'labourManagementTabOverview', label: 'Overview' },
  { key: 'rota', labelKey: 'labourManagementTabRota', label: 'Rota vs Actual' },
  { key: 'coverage', labelKey: 'labourManagementTabCoverage', label: 'Coverage' },
  { key: 'absence', labelKey: 'labourManagementTabAbsence', label: 'Absence & Cover' },
  { key: 'cost', labelKey: 'labourManagementTabCost', label: 'Labour Cost' },
  { key: 'readiness', labelKey: 'labourManagementTabReadiness', label: 'Readiness' },
];

const today = () => new Date().toISOString().slice(0, 10);

const matchesSite = (site: string, filters: LabourFilters) =>
  (filters.site === ALL_FILTER_VALUE || site === filters.site)
  && (filters.sector === ALL_FILTER_VALUE || SITE_SECTORS[site] === filters.sector);

const LabourManagement = () => {
  const { t } = useTranslation('common');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();

  const [filters, setFilters] = useState<LabourFilters>({
    date: today(),
    site: ALL_FILTER_VALUE,
    sector: ALL_FILTER_VALUE,
    servicePeriod: ALL_FILTER_VALUE,
    view: 'Exceptions',
  });
  const [activeTab, setActiveTab] = useState<LabourTab>('overview');
  const [selectedSite, setSelectedSite] = useState<string | null>(null);
  const [rotaRows, setRotaRows] = useState<RotaRow[]>(ROTA_ROWS);
  const [absenceRows, setAbsenceRows] = useState<AbsenceCoverRow[]>(ABSENCE_COVER);

  useEffect(() => {
    getCurrencyCodeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exceptionsOnly = filters.view === 'Exceptions';

  const filteredRisks = useMemo(() => LABOUR_RISKS.filter((risk) => matchesSite(risk.site, filters)
    && (filters.servicePeriod === ALL_FILTER_VALUE || risk.servicePeriod === filters.servicePeriod || risk.servicePeriod === 'All day')), [filters]);

  const filteredCoverage = useMemo(() => COVERAGE_ROWS.filter((row) => matchesSite(row.site, filters)
    && (!exceptionsOnly || row.overall !== 'Covered')), [filters, exceptionsOnly]);

  const filteredRota = useMemo(() => rotaRows.filter((row) => matchesSite(row.site, filters)
    && (filters.servicePeriod === ALL_FILTER_VALUE || row.servicePeriod === filters.servicePeriod)
    && (!exceptionsOnly || row.status !== 'good')), [rotaRows, filters, exceptionsOnly]);

  const filteredDemand = useMemo(() => DEMAND_ALIGNMENT.filter((row) => matchesSite(row.site, filters)
    && (filters.servicePeriod === ALL_FILTER_VALUE || row.servicePeriod === filters.servicePeriod)
    && (!exceptionsOnly || row.status !== 'good')), [filters, exceptionsOnly]);

  const filteredAbsence = useMemo(() => absenceRows.filter((row) => matchesSite(row.site, filters)
    && (!exceptionsOnly || row.coverStatus !== 'Covered')), [absenceRows, filters, exceptionsOnly]);

  const filteredReadinessIssues = useMemo(() => READINESS_ISSUES.filter((issue) => matchesSite(issue.site, filters)), [filters]);

  const onSelectSite = (site: string) => setSelectedSite(site);

  const onRiskAction = (risk: LabourRisk) => {
    setFilters((prev) => ({ ...prev, site: risk.site }));
    setActiveTab(risk.action.target);
  };

  const onMarkResolved = (id: string) => setRotaRows((prev) => prev.map((row) => (row.id === id ? { ...row, resolved: true, status: 'good' } : row)));

  const onAddNote = (id: string, note: string) => setRotaRows((prev) => prev.map((row) => (row.id === id ? { ...row, note } : row)));

  const onRequestCover = (row: RotaRow) => setRotaRows((prev) => prev.map((r) => (r.id === row.id
    ? { ...r, note: `${r.note ? `${r.note} \u2013 ` : ''}Cover requested` }
    : r)));

  const onFindCover = (row: AbsenceCoverRow) => setAbsenceRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, coverStatus: 'Seeking cover' } : r)));

  return (
    <PageArea>
      <PageTitle subTitle={t('labourManagementSubtitle', 'Monitor staffing coverage, labour cost, absence and operational readiness across sites.')}>
        {t('labourManagementTitle', 'Labour Management')}
      </PageTitle>

      <FiltersBar filters={filters}
        onChange={setFilters} />

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('labourManagementKpiTitle', 'Today\u2019s labour summary')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2 }}>
            {KPI_CARDS.map((card) => (
              <KpiCard key={card.key}
                data={card} />
            ))}
          </Box>
        </Box>
      </Section>

      <Box sx={{ mt: 6 }}>
        <Section>
          <Box sx={{ p: 4 }}>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mb: 3 }}>
              {t('labourManagementRisksTitle', 'Today\u2019s labour risks')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
              {filteredRisks.length === 0 && (
                <Typography variant="textSM"
                  color="text.secondary">
                  {t('labourManagementNoRisks', 'No labour risks for the current filters.')}
                </Typography>
              )}
              {filteredRisks.map((risk) => (
                <RiskCard key={risk.id}
                  risk={risk}
                  onAction={onRiskAction} />
              ))}
            </Box>
          </Box>
        </Section>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Tabs value={activeTab}
          onChange={(_e, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto">
          {TABS.map((tab) => (
            <Tab key={tab.key}
              value={tab.key}
              label={t(tab.labelKey, tab.label)} />
          ))}
        </Tabs>
      </Box>

      <Box sx={{ mt: 3 }}>
        {activeTab === 'overview' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Section>
              <Box sx={{ p: 4 }}>
                <Typography variant="textMD"
                  fontWeight={600}
                  sx={{ mb: 3 }}>
                  {t('labourManagementCoverageTitle', 'Coverage by service period')}
                </Typography>
                <CoverageTable rows={filteredCoverage}
                  onSelectSite={onSelectSite} />
              </Box>
            </Section>
            <Section>
              <Box sx={{ p: 4 }}>
                <Typography variant="textMD"
                  fontWeight={600}
                  sx={{ mb: 3 }}>
                  {t('labourManagementDemandTitle', 'Labour demand alignment')}
                </Typography>
                <DemandAlignmentTable rows={filteredDemand}
                  currencyCode={currencyCode}
                  onSelectSite={onSelectSite} />
              </Box>
            </Section>
          </Box>
        )}

        {activeTab === 'rota' && (
          <Section>
            <Box sx={{ p: 4 }}>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 3 }}>
                {t('labourManagementRotaTitle', 'Rota vs actual')}
              </Typography>
              <RotaTable rows={filteredRota}
                currencyCode={currencyCode}
                onSelectSite={onSelectSite}
                onMarkResolved={onMarkResolved}
                onAddNote={onAddNote}
                onRequestCover={onRequestCover} />
            </Box>
          </Section>
        )}

        {activeTab === 'coverage' && (
          <Section>
            <Box sx={{ p: 4 }}>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 3 }}>
                {t('labourManagementCoverageTitle', 'Coverage by service period')}
              </Typography>
              <CoverageTable rows={filteredCoverage}
                onSelectSite={onSelectSite} />
            </Box>
          </Section>
        )}

        {activeTab === 'absence' && (
          <Section>
            <Box sx={{ p: 4 }}>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 3 }}>
                {t('labourManagementAbsenceCoverTitle', 'Absence and cover management')}
              </Typography>
              <AbsenceCoverTable rows={filteredAbsence}
                onFindCover={onFindCover} />
            </Box>
          </Section>
        )}

        {activeTab === 'cost' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <Section>
              <Box sx={{ p: 4 }}>
                <Typography variant="textMD"
                  fontWeight={600}
                  sx={{ mb: 3 }}>
                  {t('labourManagementWeeklyTrendTitle', 'Weekly labour trend')}
                </Typography>
                <WeeklyTrend data={WEEKLY_TREND} />
              </Box>
            </Section>
            <Section>
              <Box sx={{ p: 4 }}>
                <Typography variant="textMD"
                  fontWeight={600}
                  sx={{ mb: 3 }}>
                  {t('labourManagementDemandTitle', 'Labour demand alignment')}
                </Typography>
                <DemandAlignmentTable rows={filteredDemand}
                  currencyCode={currencyCode}
                  onSelectSite={onSelectSite} />
              </Box>
            </Section>
          </Box>
        )}

        {activeTab === 'readiness' && (
          <Section>
            <Box sx={{ p: 4 }}>
              <Typography variant="textMD"
                fontWeight={600}
                sx={{ mb: 3 }}>
                {t('labourManagementReadinessTitle', 'Labour readiness')}
              </Typography>
              <ReadinessPanel metrics={READINESS_METRICS}
                issues={filteredReadinessIssues} />
            </Box>
          </Section>
        )}
      </Box>

      <SiteDrilldownDialog drilldown={selectedSite ? SITE_DRILLDOWNS[selectedSite] : null}
        currencyCode={currencyCode}
        onClose={() => setSelectedSite(null)} />
    </PageArea>
  );
};

export default LabourManagement;
